import {
    getUserSubscriptions,
    searchSubscriptions,
    getPlans,
    getPlan,
    getMySubscriptions,
    createSubscription,
    cancelSubscription,
    reactivateSubscription,
    changeSubscription,
    getBillingHistory,
    getSetupIntent,
    setDefaultPaymentMethod,
    replacePlan,
    createPlan,
} from './http/access-ws/subscriptions';
import type { GetPlansOptions } from './http/access-ws/subscriptions';
import type { MutationObserverOptions, QueryObserverOptions, QueryClient } from '@tanstack/query-core';
import type {
    BillingHistoryEntry,
    ChangeSubscriptionRequest,
    CreateSubscriptionPlanResponse,
    CreateSubscriptionRequest,
    CreateSubscriptionResponse,
    PaginatedSubscriptionSearchResponse,
    ReplaceSubscriptionPlanRequest,
    ReplaceSubscriptionPlanResponse,
    SetDefaultPaymentMethodResponse,
    SetupIntentResponse,
    Subscription,
    SubscriptionPlan,
    SubscriptionPlanWriteRequest,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';

// Constants
const SUBSCRIPTIONS = 'subscriptions';

export const getPlanQuery = (planId: string): QueryObserverOptions<SubscriptionPlan> => ({
    queryKey: [SUBSCRIPTIONS, 'plans', planId],
    queryFn: () => getPlan(planId),
});

export const getPlansQuery = (options: GetPlansOptions = {}): QueryObserverOptions<SubscriptionPlan[]> => ({
    queryKey: [SUBSCRIPTIONS, 'plans', options],
    queryFn: () => getPlans(options),
});

export const getMySubscriptionsQuery = (): QueryObserverOptions<Subscription[]> => ({
    queryKey: [SUBSCRIPTIONS, 'me'],
    queryFn: () => getMySubscriptions(),
});

export const getCreateSubscriptionMutation = (): MutationObserverOptions<
    CreateSubscriptionResponse,
    Error,
    CreateSubscriptionRequest
> => ({
    mutationFn: (request) => createSubscription(request),
});

export const getCancelSubscriptionMutation = (): MutationObserverOptions<
    Subscription,
    Error,
    { id: string; immediately?: boolean }
> => ({
    mutationFn: ({ id, immediately }) => cancelSubscription(id, immediately),
});

export const getReactivateSubscriptionMutation = (): MutationObserverOptions<Subscription, Error, string> => ({
    mutationFn: (id) => reactivateSubscription(id),
});

export const getChangeSubscriptionMutation = (): MutationObserverOptions<
    Subscription,
    Error,
    { id: string; request: ChangeSubscriptionRequest }
> => ({
    mutationFn: ({ id, request }) => changeSubscription(id, request),
});

export const getBillingHistoryQuery = (limit?: number): QueryObserverOptions<BillingHistoryEntry[]> => ({
    queryKey: [SUBSCRIPTIONS, 'billing-history', limit],
    queryFn: () => getBillingHistory(limit),
});

export const getSetupIntentMutation = (): MutationObserverOptions<SetupIntentResponse, Error, void> => ({
    mutationFn: () => getSetupIntent(),
});

export const getSetDefaultPaymentMethodMutation = (): MutationObserverOptions<
    SetDefaultPaymentMethodResponse,
    Error,
    string
> => ({
    mutationFn: (paymentMethodId) => setDefaultPaymentMethod(paymentMethodId),
});

export const invalidateSubscriptionsQuery = (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS] });

export const invalidateMySubscriptionsQuery = (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS, 'me'] });

export const getUserSubscriptionsQuery = (userId: string | undefined): QueryObserverOptions<Subscription[]> => ({
    queryKey: [SUBSCRIPTIONS, 'by-user', userId],
    queryFn: () => getUserSubscriptions(userId!),
    enabled: !!userId,
});

export const invalidateUserSubscriptionsQuery = (queryClient: QueryClient, userId: string) =>
    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS, 'by-user', userId] });

export const searchSubscriptionsQuery = (params?: {
    $search?: string;
    $skip?: number;
    $top?: number;
    $filter?: string;
}): QueryObserverOptions<PaginatedSubscriptionSearchResponse> => ({
    queryKey: [SUBSCRIPTIONS, 'search', params],
    queryFn: () => searchSubscriptions(params),
});

export const invalidatePlansQuery = (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS, 'plans'] });

export const getReplacePlanMutation = (): MutationObserverOptions<
    ReplaceSubscriptionPlanResponse,
    Error,
    { planId: string; request: ReplaceSubscriptionPlanRequest }
> => ({
    mutationFn: ({ planId, request }) => replacePlan(planId, request),
});

export const getCreatePlanMutation = (): MutationObserverOptions<
    CreateSubscriptionPlanResponse,
    Error,
    SubscriptionPlanWriteRequest
> => ({
    mutationFn: (request) => createPlan(request),
});
